import React, { useState, useMemo } from "react";
import { Formik, Form } from "formik";
// import emailjs from "emailjs-com";
import { 
  Box, 
  Stack, 
  Typography, 
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material";
import GenericTextField from "../generic-components/TextField/TextField";
import PhoneInputField from "../generic-components/PhoneInputField/PhoneInputField";
import CustomButton from "../generic-components/Button/Button";
import { careersFormValidationSchema } from "../../utils/careersValidationSchema";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublishIcon from "@mui/icons-material/Publish";
import DomainVerificationIcon from "@mui/icons-material/DomainVerification";
import FileUpload from "./fileUpload";
import { CircularProgress, Snackbar, Alert } from "@mui/material";
import "./index.css";

function CareersForm() {

  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [useCoverLetterText, setUseCoverLetterText] = useState(true);
  const [fileError, setFileError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    jobTitle: "",
    experience: "",
    resume: null,
    coverLetterText: "",
    coverLetterFile: null,  
    workRights: ""
  };

  const sendDataToGoogleSheet = async (payload) => {
    const sheetURL = "https://script.google.com/macros/s/AKfycbz8fcR3KVeBuZWPNUsONTXR0EKbg6fClDhc46jUevT0ROiGSmxaBrG1FCLErxfWcvhM/exec";

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    const res = await fetch(sheetURL, {
      method: "POST",
      body: formData,
    });

    const result = await res.json();

    if (result.result !== "success") {
      throw new Error(result.error || "Failed to save data in Google Sheet");
    }
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'jobTitle', 'experience', 'resume', 'workRights'];
      const missingFields = requiredFields.filter(field => !values[field]);
      
      if (missingFields.length > 0) {
        console.log("Missing required fields:", missingFields);
        setSubmitting(false);
        return;
      }
      
      if (fileError) {
        console.log("Form submission blocked due to file error:", fileError);
        setSubmitting(false);
        return;
      }
      
      console.log("Form Values:", values);
      
      // -----------------------------------
      // 1. UPLOAD PDF FILES TO GOOGLE DRIVE FIRST
      // -----------------------------------
      const uploadFileToDrive = async (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const base64String = event.target.result.split(",")[1];

            try {
              const res = await fetch("https://script.google.com/macros/s/AKfycbxeyOha_GQrxCcaqMTewQIs7dYY_2K3YfHEPji_YYBNncfN6EsHPARX-y4GR1ARDXj-Gw/exec", {
                method: "POST",
                body: JSON.stringify({
                  base64: base64String,
                  fileName: file.name,
                  fileType: file.type,
                }),
                headers: {
                  "Content-Type": "text/plain;charset=utf-8",
                },
              });

              const result = await res.json();
              if (result.success) {
                console.log(`✅ File uploaded: ${result.fileUrl}`);
                resolve(result.fileUrl);
              } else {
                reject(result.error);
              }
            } catch (err) {
              reject(err);
            }
          };
          reader.readAsDataURL(file);
        });
      };

      let resumeUrl = '';
      if (resumeFile) {
        resumeUrl = await uploadFileToDrive(resumeFile);
      }

      let coverLetterUrl = '';
      if (!useCoverLetterText && coverLetterFile) {
        coverLetterUrl = await uploadFileToDrive(coverLetterFile);
      }

      // -----------------------------------
      // 2. SEND EMAIL WITH FILE LINKS INCLUDED
      // -----------------------------------
      /*
      const templateParams = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        jobTitle: values.jobTitle,
        experience: values.experience,
        workRights: values.workRights,
        coverLetterText: useCoverLetterText ? values.coverLetterText : '',  // Only text if provided; otherwise empty (use coverLetterUrl for file)
        resumeUrl: resumeUrl,  // New: Include resume link
        coverLetterUrl: coverLetterUrl,  // New: Include cover letter link if file uploaded
      };

      await emailjs.send("service_94q8475", "template_5wdxl95", templateParams, "gQZ0UvulTbk-FwdS4");
      console.log("✅ Email sent successfully");
      */
      await sendDataToGoogleSheet({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        jobTitle: values.jobTitle,
        experience: values.experience,
        resumeUrl,
        coverLetterText: useCoverLetterText ? values.coverLetterText : "",
        coverLetterUrl,
        workRights: values.workRights,
      });

      // Only reset form after successful submission
      setOpenSnackbar(true);
      setResumeFile(null);
      setCoverLetterFile(null);
      setFileError("");
      setUseCoverLetterText(true);
      resetForm();
    } catch (error) {
      console.error("Error during form submission:", error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Function to validate file type
  const validateFileType = (file) => {
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (file && !validTypes.includes(file.type)) {
      return false;
    }
    return true;
  };

  const handleResumeChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      if (!validateFileType(file)) {
        setFileError("Only PDF and Word documents (.pdf, .doc, .docx) are allowed");
        setFieldValue("resume", null);
        setResumeFile(null);
      } else {
        setFileError("");
        setResumeFile(file);
        setFieldValue("resume", file.name);
      }
    }
  };

  const handleCoverLetterFileChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      if (!validateFileType(file)) {
        setFileError("Only PDF and Word documents (.pdf, .doc, .docx) are allowed");
        setFieldValue("coverLetterFile", null);
        setCoverLetterFile(null);
      } else {
        setFileError("");
        setCoverLetterFile(file);
        setFieldValue("coverLetterFile", file.name);
      }
    } else {
      // Handle case when user cancels file selection
      setCoverLetterFile(null);
      setFieldValue("coverLetterFile", null);
    }
  };
  
  // Define experience options directly inside useMemo to avoid dependency issues
  const memoizedExperienceOptions = useMemo(() => [
    { value: "0-6 months", label: "0-6 months" },
    { value: "1+ years", label: "1+ years" },
    { value: "2+ years", label: "2+ years" },
    { value: "3+ years", label: "3+ years" },
    { value: "4+ years", label: "4+ years" },
    { value: "5+ years", label: "5+ years" },
    { value: "6+ years", label: "6+ years" },
    { value: "7+ years", label: "7+ years" },
    { value: "8+ years", label: "8+ years" },
    { value: "9+ years", label: "9+ years" },
    { value: "10+ years", label: "10+ years" }
  ], []);

  const toggleCoverLetterType = (setFieldValue) => {
    setUseCoverLetterText(prev => {
      const newValue = !prev;
      
      // Clear the field that's being switched away from
      if (newValue) {
        // Switching to text, clear file
        setCoverLetterFile(null);
        setFieldValue("coverLetterFile", null);
      } else {
        // Switching to file, clear text
        setFieldValue("coverLetterText", "");
      }
      
      return newValue;
    });
  };


  return (
    <Box className="careers-page-container">
      <Box className="careers-header">
        <Typography variant="h2" className="careers-page-title">
          Join Our Team
        </Typography>
        <Typography variant="body1" className="careers-page-subtitle">
          We're looking for talented individuals to help us grow
        </Typography>
      </Box>

      <Box className="careers-content">
        <Stack direction={{ xs: "column", md: "row" }} spacing={4} className="careers-main-content">
          {/* Left Section */}
          <Box className="careers-info-section">
            <Typography variant="h4" className="careers-section-title">
              Why Work With Us
            </Typography>
            <Box className="careers-perks">
              <Box className="career-perk-item">
                <Typography variant="h6">Professional Growth</Typography>
                <Typography variant="body2">
                  We invest in your development and offer clear career progression paths.
                </Typography>
              </Box>
              <Box className="career-perk-item">
                <Typography variant="h6">Collaborative Culture</Typography>
                <Typography variant="body2">
                  Work alongside talented professionals in a supportive environment.
                </Typography>
              </Box>
              <Box className="career-perk-item">
                <Typography variant="h6">Work-Life Balance</Typography>
                <Typography variant="body2">
                  Flexible working arrangements to help you maintain balance.
                </Typography>
              </Box>
              <Box className="career-perk-item">
                <Typography variant="h6">Competitive Benefits</Typography>
                <Typography variant="body2">
                  Attractive salary packages and comprehensive benefits.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Section */}
          <Box className="careers-form-section">
            <Typography variant="h4" className="careers-section-title">
              Apply Now
            </Typography>
            
            <Formik
              initialValues={initialValues}
              validationSchema={careersFormValidationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {({ 
                handleChange, 
                handleBlur, 
                values, 
                errors, 
                touched, 
                isSubmitting,
                setFieldValue
              }) => (
                <Form className="careers-form">
                  <Stack spacing={3}>
                    <Stack 
                      direction={{ xs: "column", sm: "row" }} 
                      spacing={2}
                    >
                      <Box className="form-field" sx={{ width: { xs: '100%', sm: '50%' } }}>
                        <GenericTextField
                          name="firstName"
                          label="First Name"
                          icon={<PersonIcon className="field-icon" />}
                          placeholder="Enter your First Name"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Box>
                      <Box className="form-field" sx={{ width: { xs: '100%', sm: '50%' } }}>
                        <GenericTextField
                          name="lastName"
                          label="Last Name"
                          icon={<PersonIcon className="field-icon" />}
                          placeholder="Enter your Last Name"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Box>
                    </Stack>

                    <Box className="form-field">
                      <GenericTextField
                        name="email"
                        label="Email Address"
                        icon={<EmailIcon className="field-icon" />}
                        placeholder="Enter your Email Address"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        fullWidth
                      />
                    </Box>

                    <Box className="form-field">
                      <PhoneInputField
                        name="phone"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        fullWidth
                      />
                      {touched.phone && errors.phone && (
                        <Typography color="error" variant="caption" className="error-message">
                          {errors.phone}
                        </Typography>
                      )}
                    </Box>

                    <Box className="form-field">
                      <GenericTextField
                        name="jobTitle"
                        label="Job Title / Role Applying For"
                        icon={<WorkIcon className="field-icon" />}
                        placeholder="Enter the position you're applying for"
                        value={values.jobTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.jobTitle && Boolean(errors.jobTitle)}
                        helperText={touched.jobTitle && errors.jobTitle}
                        fullWidth
                      />
                    </Box>

                    <Box className="form-field">
                      <GenericTextField
                        name="experience"
                        label="Years of Experience"
                        icon={<AccessTimeIcon className="field-icon" />}
                        select
                        options={memoizedExperienceOptions}
                        value={values.experience}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.experience && Boolean(errors.experience)}
                        helperText={touched.experience && errors.experience}
                        fullWidth
                      />
                    </Box>

                    <Box className="form-field file-upload-field">
                      <FileUpload
                        name="resume"
                        accept=".pdf,.doc,.docx"
                        icon={<PublishIcon className="field-icon" />}
                        value={values.resume}
                        onChange={(event) => handleResumeChange(event, setFieldValue)}
                        onBlur={handleBlur}
                        error={(touched.resume && Boolean(errors.resume)) || Boolean(fileError)}
                        // helperText={touched.resume && errors.resume}
                      />
                      {resumeFile && (
                        <Typography variant="body2" className="file-name">
                          Selected file: {resumeFile.name}
                        </Typography>
                      )}
                      {fileError && (
                        <Typography color="error" variant="caption" className="error-message">
                          {fileError}
                        </Typography>
                      )}
                      {touched.resume && errors.resume && (
                        <Typography color="error" variant="caption" className="error-message">
                          {errors.resume}
                        </Typography>
                      )}
                      <Typography variant="caption" className="file-format-info">
                        Accepted formats: PDF, DOC, DOCX
                      </Typography>
                    </Box>

                    <Box className="form-field">
                      <Box className="cover-letter-toggle">
                        <Typography variant="body2" className="cover-letter-label">
                          Cover Letter (Optional):
                        </Typography>
                        <Box className="toggle-buttons">
                          <button
                            type="button"
                            className={`toggle-button ${useCoverLetterText ? 'active' : ''}`}
                            onClick={() => {
                              if (!useCoverLetterText) toggleCoverLetterType(setFieldValue);
                            }}
                          >
                            Text
                          </button>
                          <button
                            type="button"
                            className={`toggle-button ${!useCoverLetterText ? 'active' : ''}`}
                            onClick={() => {
                              if (useCoverLetterText) toggleCoverLetterType(setFieldValue);
                            }}
                          >
                            Upload File
                          </button>
                        </Box>
                      </Box>

                      {useCoverLetterText ? (
                        <GenericTextField
                          name="coverLetterText"
                          multiline={true}
                          rows={4}
                          placeholder="Write your cover letter here (optional)"
                          value={values.coverLetterText || ""}  // Handle possible null value
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                        />
                      ) : (
                        <>
                          <FileUpload
                            name="coverLetterFile"
                            accept=".pdf,.doc,.docx"
                            icon={<PublishIcon className="field-icon" />}
                            value={values.coverLetterFile}
                            onChange={(event) => handleCoverLetterFileChange(event, setFieldValue)}
                            onBlur={handleBlur}
                          />
                          <Typography variant="caption" className="file-format-info">
                            Accepted formats: PDF, DOC, DOCX
                          </Typography>
                        </>
                      )}
                      
                      {!useCoverLetterText && coverLetterFile && (
                        <Typography variant="body2" className="file-name">
                          Selected file: {coverLetterFile.name}
                        </Typography>
                      )}
                    </Box>

                    <Box className="form-field">
                      <FormLabel component="legend" className="radio-group-label">
                        Do you have the right to work in Australia?
                      </FormLabel>
                      <RadioGroup
                        name="workRights"
                        value={values.workRights}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        row
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio className="radio-button" />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio className="radio-button" />}
                          label="No"
                        />
                      </RadioGroup>
                      {touched.workRights && errors.workRights && (
                        <Typography color="error" variant="caption" className="error-message">
                          {errors.workRights}
                        </Typography>
                      )}
                    </Box>

                    <Box className="submit-button-container">
                    <CustomButton
                      type="submit"
                      text={isSubmitting ? "Submitting..." : "Submit Application"}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <DomainVerificationIcon className="submit-btn-icon" />
                        )
                      }
                      className="submit-btn"
                      disabled={isSubmitting}
                    />                    
                    </Box>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        </Stack>
      </Box>
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={4000} 
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: "100%" }}>
          🎉 Your application has been submitted successfully!
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default CareersForm;